import { useEffect, useState } from "react";
import { ExternalLink, ShieldCheck, AlertTriangle, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/pages/app/AppShell";
import { api, errMsg, fmtDate, money } from "@/lib/api";

const STATUS = {
  awaiting_onboarding: ["Waiting for payout setup", "text-amber-200 bg-amber-300/10"],
  pending: ["Queued", "text-slate-200 bg-[#2A1240]/10"],
  transferred: ["Sent to Stripe", "text-emerald-300 bg-emerald-400/10"],
  failed: ["Failed", "text-red-300 bg-red-400/10"],
};

export function ConnectCard({ onChange }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/connect/status").then((r) => { setS(r.data); onChange?.(); }).catch(() => setS({ connected: false }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = async (path) => {
    setBusy(true);
    try { const { data } = await api.post(path); window.location.href = data.url; } catch (e) { toast.error(errMsg(e), { duration: 9000 }); setBusy(false); }
  };
  const ready = s?.payouts_enabled;
  return (
    <Card className="p-6 mb-4 flex flex-col md:flex-row md:items-center gap-5" data-testid="connect-card">
      <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-full ${ready ? "bg-emerald-400/15 text-emerald-300" : "bg-[#E0218A]/15 text-[#8E2BC4]"}`}>
        {ready ? <ShieldCheck className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
      </span>
      <div className="flex-1">
        <p className="font-display text-xl text-[#2A1240]" data-testid="connect-status">
          {!s ? "Checking payout setup…" : ready ? "Automatic payouts are on" : s.connected ? "Finish your payout setup" : "Set up automatic payouts"}
        </p>
        <p className="mt-1 text-sm text-slate-300">
          {ready ? "Each paid invoice, deposit or milestone is transferred to your Stripe account automatically and in full — payers cover the transaction fee."
            : "Connect a bank account through Stripe (about 5 minutes). Paid milestones are then sent to you automatically."}
        </p>
        {s?.requirements?.length > 0 && !ready && <p className="mt-1 text-xs text-amber-200">Stripe still needs: {s.requirements.join(", ").replace(/_/g, " ")}</p>}
      </div>
      {ready
        ? <button className="btn-ghost !py-2.5" disabled={busy} onClick={() => go("/connect/dashboard")} data-testid="connect-dashboard-button"><ExternalLink className="h-4 w-4" />Stripe payouts dashboard</button>
        : <button className="btn-gold !py-2.5" disabled={busy || !s} onClick={() => go("/connect/onboard")} data-testid="connect-onboard-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{s?.connected ? "Continue setup" : "Set up payouts"}</button>}
    </Card>
  );
}

export function PayoutRows({ payouts, reload }) {
  const retry = async (p) => {
    try { await api.post(`/payouts/${p.id}/retry`); toast.success("Payout retried"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="divide-y divide-[#2A1240]/10" data-testid="payouts-list">
      {!payouts.length && <p className="p-10 text-center text-slate-300">Payouts appear here once an invoice is paid.</p>}
      {payouts.map((p) => {
        const [label, cls] = STATUS[p.status] || [p.status, "bg-[#2A1240]/10 text-slate-200"];
        return (
          <div key={p.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`payout-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-[#2A1240]">{p.invoice_number} · {p.title}</p>
              <p className="text-xs text-slate-400">{fmtDate(p.created_at)} · payer paid {money(p.gross, 2)} · fee {money(p.fee, 2)}{p.stripe_transfer_id ? ` · ${p.stripe_transfer_id}` : ""}</p>
              {p.error && <p className="mt-1 text-xs text-red-300">{p.error}</p>}
            </div>
            <p className="font-mono text-emerald-300">{money(p.net, 2)}</p>
            <span className={`rounded-full px-3 py-1 text-xs ${cls}`} data-testid={`payout-status-${p.id}`}>{label}</span>
            {p.status === "failed" && <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => retry(p)} data-testid={`payout-retry-${p.id}`}><RotateCcw className="h-3.5 w-3.5" />Retry</button>}
          </div>
        );
      })}
    </Card>
  );
}
